/**
 * ScenarioList - Display list of cold storage scenarios as a table
 *
 * Uses ProjectTable components from shared-ui for consistent styling
 * with retro/light theme support.
 */

import React from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    TableRow,
    TableCell,
    alpha,
    Skeleton,
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as DuplicateIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Assessment as ScenarioIcon,
    AcUnit as ColdIcon,
} from '@mui/icons-material';
import {
    useThemeMode,
    vortxColors,
    SectionCard,
    useResponsive,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';

/**
 * Summarize zones: count + total compressor kW
 */
function formatZoneSummary(zones) {
    if (!zones || zones.length === 0) {
        return 'No zones configured';
    }

    const totalKw = zones.reduce((sum, z) => {
        return sum + (z.compressor?.electrical_power_kw || 0);
    }, 0);

    const zoneCount = zones.length;
    return `${zoneCount} zone${zoneCount !== 1 ? 's' : ''}, ${Math.round(totalKw)} kW total`;
}

/**
 * SkeletonTableRows - Loading placeholder rows for the scenario table
 */
function SkeletonTableRows({ count = 3 }) {
    return Array.from({ length: count }).map((_, i) => (
        <TableRow key={`skeleton-${i}`}>
            <TableCell><Skeleton variant="text" width="60%" /></TableCell>
            <TableCell><Skeleton variant="text" width="50%" /></TableCell>
            <TableCell><Skeleton variant="rectangular" width={90} height={22} sx={{ borderRadius: 10 }} /></TableCell>
            <TableCell><Skeleton variant="text" width="50%" /></TableCell>
            <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                </Box>
            </TableCell>
        </TableRow>
    ));
}

/**
 * ScenarioList Component
 *
 * @param {Object} props
 * @param {Array} props.scenarios - List of scenario objects
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.onSelect - Callback when scenario is selected
 * @param {Function} props.onCreate - Callback to create new scenario
 * @param {Function} props.onDuplicate - Callback to duplicate a scenario
 * @param {Function} props.onDelete - Callback to delete a scenario
 * @param {string} props.selectedId - Currently selected scenario ID
 */
function ScenarioList({
    scenarios = [],
    isLoading = false,
    onSelect,
    onCreate,
    onDuplicate,
    onDelete,
    selectedId,
}) {
    const { isMobile } = useResponsive();
    const { isDark } = useThemeMode();

    return (
        <SectionCard
            title="Scenarios"
            icon={<ScenarioIcon />}
            action={
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onCreate}
                    sx={{
                        borderColor: vortxColors.gold,
                        color: vortxColors.gold,
                        '&:hover': {
                            borderColor: vortxColors.gold,
                            bgcolor: alpha(vortxColors.gold, 0.1),
                        },
                    }}
                >
                    {isMobile ? 'New' : 'New Scenario'}
                </Button>
            }
        >
            {isLoading ? (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell>Name</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Zones</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Status</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Results</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right">Actions</ProjectTableHeaderCell>
                    </ProjectTableHead>
                    <ProjectTableBody>
                        <SkeletonTableRows />
                    </ProjectTableBody>
                </ProjectTableContainer>
            ) : scenarios.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ColdIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>
                        No scenarios yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Create a scenario to model different cold storage zone configurations
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onCreate}
                    >
                        Create First Scenario
                    </Button>
                </Box>
            ) : (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell>Name</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Zones</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Status</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Results</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right">Actions</ProjectTableHeaderCell>
                    </ProjectTableHead>
                    <ProjectTableBody>
                        {scenarios.map((scenario) => {
                            const hasResults = scenario.results && Object.keys(scenario.results).length > 0;
                            const isSelected = selectedId === scenario.id;

                            return (
                                <TableRow
                                    key={scenario.id}
                                    className="vx-slide-in-up vx-state-transition"
                                    onClick={() => onSelect(scenario)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: isSelected
                                            ? alpha(vortxColors.gold, isDark ? 0.15 : 0.08)
                                            : 'transparent',
                                        '&:hover': {
                                            bgcolor: alpha(vortxColors.gold, isDark ? 0.1 : 0.05),
                                        },
                                        '& td': {
                                            borderBottom: isDark
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 200,
                                            }}
                                        >
                                            {scenario.name || 'Unnamed Scenario'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {formatZoneSummary(scenario.zones)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={hasResults ? 'Optimized' : 'Not optimized'}
                                            size="small"
                                            color={hasResults ? 'success' : 'default'}
                                            sx={{
                                                fontSize: '0.7rem',
                                                height: 22,
                                                '& .MuiChip-label': { px: 1 },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {hasResults ? (
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {scenario.results.peak_demand_kw != null
                                                    ? `${Math.round(scenario.results.peak_demand_kw).toLocaleString()} kW`
                                                    : '\u2014'}
                                                {' / '}
                                                {scenario.results.savings_pct != null
                                                    ? `${scenario.results.savings_pct.toFixed(1)}% savings`
                                                    : '\u2014'}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.disabled">
                                                {'\u2014'}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onSelect(scenario)}
                                                    sx={{
                                                        color: vortxColors.gold,
                                                        '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) },
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Duplicate">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDuplicate(scenario.id)}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&:hover': { color: vortxColors.gold, bgcolor: alpha(vortxColors.gold, 0.1) },
                                                    }}
                                                >
                                                    <DuplicateIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDelete(scenario.id)}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&:hover': { color: 'error.main', bgcolor: alpha('#d32f2f', 0.1) },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </ProjectTableBody>
                </ProjectTableContainer>
            )}
        </SectionCard>
    );
}

export default ScenarioList;
